"use client"

import { Suspense, useEffect, useMemo, useRef, useState } from "react"
import { usePrerenderSafeSearchParams } from "@/hooks/use-prerender-safe-search-params"
import { SearchParamsHydrationGate } from "@/components/ssr/search-params-hydration-gate"
import { Layers, ArrowRight } from "lucide-react"
import { motion } from "motion/react"
import {
  HeadlineHero,
  LawnPrefillBanner,
  RelatedTools,
  ResultEmailCaptureCard,
  ToolPageLoadingFallback,
} from "@/components/tools/shared"
import { BreadcrumbComponent } from "@/components/ui/breadcrumb"
import { ProductRecommendations } from "@/components/tools/product-recommendations"
import { NewsletterSignupModal } from "@/components/ui/newsletter-signup-modal"
import { SodForm } from "@/components/tools/sod-form"
import { SodResults } from "@/components/tools/sod-results"
import { getAngiAffiliateUrl } from "@/data/angi-affiliate"
import { SERVICE_QUOTE_SURFACES } from "@/data/service-quote-funnels"
import { calculateSod, type SodGrassId, type SodUnit } from "@/lib/sod/calculator"
import { useDefaultLawn } from "@/hooks/use-default-lawn"
import { useAuth } from "@/contexts/AuthContext"
import { useNewsletterPrompt } from "@/hooks/useNewsletterPrompt"
import { useFromTool } from "@/hooks/use-from-tool"
import { analytics, stashEmailCaptureAttribution, trackEvent } from "@/lib/analytics"
import { useServiceLeadCtaViewed } from "@/hooks/use-service-lead-cta-viewed"
import { buildToolUrl } from "@/lib/result-tool-urls"
import { FADE_UP, FADE_TRANSITION } from "@/lib/motion-presets"
import { Reveal } from "@/components/motion"

const VALID_GRASS_IDS: SodGrassId[] = ["bermuda", "zoysia", "staug", "centipede", "fescue", "kbg", "notsure"]

/**
 * Normalize any grass hint (compact id, guide slug, or display name) to a sod
 * variety id. Falls back to 'notsure' so a stray value never breaks pricing.
 */
function normalizeSodGrass(value: string | null | undefined): SodGrassId {
  if (!value) return "notsure"
  const v = value.toLowerCase()
  if (VALID_GRASS_IDS.includes(v as SodGrassId)) return v as SodGrassId
  if (v.includes("bermuda")) return "bermuda"
  if (v.includes("zoysia")) return "zoysia"
  if (v.includes("augustine") || v.includes("staug")) return "staug"
  if (v.includes("centipede")) return "centipede"
  if (v.includes("fescue")) return "fescue"
  if (v.includes("kentucky") || v.includes("bluegrass") || v === "kbg") return "kbg"
  return "notsure"
}

function toArea(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === "") return null
  const n = typeof value === "number" ? value : Number(value)
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null
}

function SodCalculatorContent() {
  const searchParams = usePrerenderSafeSearchParams()
  useFromTool("sod-calculator")
  const { lawns, selectedLawnId, setSelectedLawnId, prefill: lawnPrefill } = useDefaultLawn()
  const { subscription } = useAuth()
  const { trackUsage, showNewsletterModal, closeNewsletterModal, usageCount } = useNewsletterPrompt()

  // Lazy initial state from deep-link params, then saved lawn, then defaults.
  const [area, setArea] = useState<number>(
    () => toArea(searchParams.get("size")) ?? toArea(lawnPrefill.lawnSizeSqft) ?? 5000,
  )
  const [grassId, setGrassId] = useState<SodGrassId>(
    () => normalizeSodGrass(searchParams.get("grass") ?? lawnPrefill.grassType),
  )
  const [zip, setZip] = useState<string>(
    () => (searchParams.get("zip") ?? lawnPrefill.zipCode ?? "").replace(/[^0-9]/g, "").slice(0, 5),
  )
  const [unit, setUnit] = useState<SodUnit>("pallets")

  const result = useMemo(
    () => calculateSod({ areaSqFt: area, grassId, zipCode: zip }),
    [area, grassId, zip],
  )

  // Fire usage + analytics once, after the first meaningful result renders.
  const trackedRef = useRef(false)
  useEffect(() => {
    if (trackedRef.current || !area) return
    trackedRef.current = true
    trackUsage("sod-calculator")
    analytics.calculatorUsed("sod", {
      inputs: { grass_type: grassId, zip_code: zip || undefined, area_sqft: area },
      result: {
        pallets: result.pallets,
        pieces: result.pieces,
        order_area_sqft: result.orderAreaSqFt,
        material_mid: Math.round(result.material.mid),
        installed_mid: Math.round(result.installed.mid),
        region: result.regionLabel,
      },
      userTier: subscription?.isPremium ? "premium" : "free",
      source: "live_update",
    })
    // Intentionally run once on mount; the ref guards re-fires.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Direct Angi affiliate handoff (2026-07-18 pivot); sid splits the two
  // sod-calculator placements apart in CJ commission reports. Both CTAs fire
  // the standard viewed/clicked event pair so PostHog stays joinable with the
  // CJ sid report (these two placements were click-blind until 2026-08-01).
  const quoteHref = getAngiAffiliateUrl("sod_installation", "sod_results")
  const dockQuoteHref = getAngiAffiliateUrl("sod_installation", "sod_sticky_dock")
  const resultsCtaProps = {
    service_type: "sod_installation",
    source_page: "/tools/sod-calculator",
    source_placement: "sod_results",
  }
  const dockCtaProps = {
    service_type: "sod_installation",
    source_page: "/tools/sod-calculator",
    source_placement: "sod_sticky_dock",
  }
  const resultsCtaRef = useServiceLeadCtaViewed<HTMLAnchorElement>(resultsCtaProps)
  const dockCtaRef = useServiceLeadCtaViewed<HTMLAnchorElement>(dockCtaProps)

  return (
    <div className="min-h-screen bg-linear-to-br from-green-50 via-white to-gray-50">
      <Reveal onMount y={6} className="max-w-6xl mx-auto px-4 md:px-6 lg:px-8 pt-4 md:pt-8 pb-24 md:pb-16">
        <BreadcrumbComponent
          items={[
            { label: "Home", href: "/" },
            { label: "Tools", href: "/tools" },
            { label: "Sod Calculator" },
          ]}
        />

        {/* Hero */}
        <div className="mt-6 md:mt-8">
          <HeadlineHero
            eyebrow="Pallets, pieces & cost"
            eyebrowIcon={<Layers className="h-3.5 w-3.5" aria-hidden="true" />}
            title="How much sod do you need,"
            accent="and what will it cost?"
            subtitle="Enter your lawn size and we'll turn it into pallets and pieces with a 10% cutting allowance, price the material and professional install for your region, and show you how it compares to seeding."
            align="left"
          />
        </div>

        <LawnPrefillBanner lawns={lawns} selectedLawnId={selectedLawnId} onSelect={setSelectedLawnId} />

        {/* Tool: form + results rail */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(330px, 1fr))",
            gap: 28,
            alignItems: "start",
            marginTop: 28,
          }}
        >
          <SodForm
            area={area}
            grassId={grassId}
            zip={zip}
            regionLabel={result.regionLabel}
            regionKnown={result.regionKnown}
            onAreaChange={setArea}
            onGrassChange={setGrassId}
            onZipChange={setZip}
          />

          <div className="flex flex-col gap-4 lg:sticky lg:top-6">
            {area >= 100 ? (
              <>
                <SodResults
                  result={result}
                  unit={unit}
                  onUnitChange={setUnit}
                  ctaHref={SERVICE_QUOTE_SURFACES.sodCalculator ? quoteHref : undefined}
                  ctaRef={resultsCtaRef}
                  onCtaClick={() =>
                    trackEvent("service_lead_angi_clicked", { ...resultsCtaProps, placement: "sod_results" })
                  }
                />
                <ResultEmailCaptureCard
                  source="tool_result_sod"
                  toolDisplayName="sod estimate"
                  subcopy="Seasonal sod-timing notes and prep checklists, sent as your planting window opens."
                  grassType={grassId}
                  zipCode={zip || undefined}
                  accountLead={{
                    redirectTo: buildToolUrl("sod-calculator", {
                      grassType: grassId,
                      zip: zip || undefined,
                      lawnSize: area,
                    }),
                    headline: "Save my sod estimate",
                    subcopy: "Keep this sod estimate in a free account and track your lawn over time.",
                    // Grass-aware (grassType + zipCode passed) so the subscribe route
                    // sends the 12-month calendar. Name it, per .claude/rules/tools-pages.md.
                    dividerLabel: "or get your 12-month calendar by email",
                    onBeforeOAuth: () => stashEmailCaptureAttribution("tool_result_sod"),
                  }}
                  onCaptured={closeNewsletterModal}
                />
              </>
            ) : (
              <div role="status" className="rounded-3xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
                Enter at least 100 sq ft to see pallets, pieces, weight, and cost.
              </div>
            )}
          </div>
        </div>

        <RelatedTools
          currentToolId="sod-calculator"
          prefillValues={{
            zip: zip || lawnPrefill.zipCode,
            grassType: grassId,
            lawnSize: area,
          }}
        />

        <div className="mt-8">
          <ProductRecommendations
            categories={["Fertilizers & Soil Health", "Lawn Mowers & Maintenance Tools"]}
            title="Prep tools for a new sod lawn"
            description="Starter fertilizer, soil rakes, and rollers that help fresh sod knit in"
            trackingContext="sod-calculator-result"
            keywords={["starter fertilizer", "soil", "roller", "rake"]}
            compact
          />
        </div>
      </Reveal>

      {/* Mobile sticky dock — sole action is the quote CTA. LIVE (gated by
          SERVICE_QUOTE_SURFACES.sodCalculator, currently true) — an earlier
          comment said "paused", which was stale. Anchor ads are blocked on
          /tools/* so this dock owns the bottom edge. */}
      {SERVICE_QUOTE_SURFACES.sodCalculator && area >= 100 && <div
        className="md:hidden"
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          display: "flex",
          alignItems: "center",
          gap: 14,
          padding: "12px 16px calc(12px + env(safe-area-inset-bottom))",
          background: "rgba(255,255,255,0.92)",
          backdropFilter: "blur(8px)",
          borderTop: "1px solid #e5e7eb",
          boxShadow: "0 -8px 30px rgba(0,0,0,0.08)",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 800,
              color: "#111827",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {result.pallets} {result.pallets === 1 ? "pallet" : "pallets"} · {result.sodMaterialLabel.replace(" DIY", "")} material
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#15803d" }}>
            Installed: {result.installed.label}
          </div>
        </div>
        <a
          ref={dockCtaRef}
          href={dockQuoteHref}
          target="_blank"
          rel="sponsored noopener noreferrer"
          onClick={() =>
            trackEvent("service_lead_angi_clicked", { ...dockCtaProps, placement: "sod_sticky_dock" })
          }
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            minHeight: 44,
            padding: "0 18px",
            borderRadius: 9999,
            fontFamily: "inherit",
            fontSize: 14,
            fontWeight: 700,
            color: "#fff",
            textDecoration: "none",
            whiteSpace: "nowrap",
            background: "linear-gradient(90deg, #16a34a 0%, #059669 100%)",
            boxShadow: "0 8px 20px -6px rgba(22,163,74,0.30)",
          }}
        >
          Get my quote
          <ArrowRight aria-hidden="true" style={{ width: 15, height: 15 }} />
        </a>
      </div>}

      <NewsletterSignupModal
        isOpen={showNewsletterModal}
        onClose={closeNewsletterModal}
        toolName="Sod Calculator"
        usageCount={usageCount}
        grassType={grassId}
        zipCode={zip || undefined}
      />
    </div>
  )
}

export function SodCalculatorPageContent() {
  return (
    <Suspense fallback={<ToolPageLoadingFallback toolId="sod-calculator" />}>
      <SearchParamsHydrationGate>
        <SodCalculatorContent />
      </SearchParamsHydrationGate>
    </Suspense>
  )
}
